/**
 * The first entry of a page after the first (RFC 0360 3c).
 *
 * A session's log is a chain of page documents. Most of what a page needs
 * from the pages before it is carried as the entries themselves (see
 * `paging.ts`); this entry carries what cannot be, the World agent's stop
 * reports, folded, because that log holds thousands of them, plus the few
 * numbers that say what was closed.
 *
 * It sorts FIRST on its page: no parents, and a `createdAt` below every
 * carried entry, so a fold that starts from it reads it before anything else.
 */

import type { SessionChanges } from './projection-changes'
import { topoOrder } from './projection-order'
import type { Entry, EntryId, LogDoc, TimestampMs } from './types'

export const PAGE_SEED_EXTENSION = 'mod.page_seed' as const

/** A stop report as `projectStopReports` returns it, carried across a page. */
export interface SeededStopReport {
  entryId: string
  sessionId: string
  runId: string
  interactionId: string
  status: string
  summary: string
  preview: string
  at: number
  seen: boolean
  verdict?: string
  note?: string
  /** When the report landed, for a later decision's cutoff. */
  landedAt: TimestampMs
}

export interface PageSeed {
  /** The page this seed closes, and its heads at close. */
  closedPage: string
  closedHeads: EntryId[]
  /** Entries left on the closed page, for the migration report and `load earlier`. */
  closedEntries: number
  /** The closed page's index; this page is one more. */
  closedIndex: number
  /** The World agent's stop reports folded over every page before this one. */
  stopReports: SeededStopReport[]
  /** `stopHearings` over every page before this one. */
  stopHearings: Record<string, number>
  /** What every page before this one changed (`changesFromTranscript`),
   *  without the tail this page carries, so the live page's own fold joins
   *  it with nothing counted twice. */
  changes: SessionChanges | null
}

/**
 * A stored value as plain data. The seed is written through `encodeData`, so
 * its strings come back as scalars, and a reader that compares or folds them
 * needs strings (RFC 0356): unwrapped once, here, at the boundary.
 */
function unwrap(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(unwrap)
  if (v && typeof v === 'object') {
    const keys = Object.keys(v)
    if (keys.length === 1 && keys[0] === 'val')
      return String((v as { val: unknown }).val)
    const out: Record<string, unknown> = {}
    for (const [k, x] of Object.entries(v as Record<string, unknown>))
      out[k] = unwrap(x)
    return out
  }
  return v
}

/** The seed on this page, or null on a session's first page. */
export function pageSeedOf(doc: LogDoc): PageSeed | null {
  if (!doc?.entries) return null
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'extension') continue
    if (p.payload.extensionType !== PAGE_SEED_EXTENSION) continue
    const raw = unwrap(p.payload.payload) as Record<string, unknown>
    return {
      closedPage: String(raw.closedPage ?? ''),
      closedHeads: Array.isArray(raw.closedHeads)
        ? raw.closedHeads.map(String)
        : [],
      closedEntries: Number(raw.closedEntries ?? 0) || 0,
      closedIndex: Number(raw.closedIndex ?? 0) || 0,
      stopReports: Array.isArray(raw.stopReports)
        ? (raw.stopReports as SeededStopReport[])
        : [],
      stopHearings:
        raw.stopHearings && typeof raw.stopHearings === 'object'
          ? (raw.stopHearings as Record<string, number>)
          : {},
      changes:
        raw.changes && typeof raw.changes === 'object'
          ? (raw.changes as SessionChanges)
          : null,
    }
  }
  return null
}

/** Whether this entry is a page seed. */
export function isPageSeed(e: Entry): boolean {
  return (
    e.payload.type === 'extension' &&
    e.payload.payload.extensionType === PAGE_SEED_EXTENSION
  )
}
